// apps/frontend-vario-otto/src/views/tabs/offers-import.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { ensureOttoStyles, OTTO_ICON, ottoEscapeHtml } from '../otto-ui.js';
import { parseOfferImport, type OfferImportParse } from '../../lib/parse-offer-import.js';
import { awaitJob, type JobRow } from '../../lib/await-job.js';

interface Ctx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

interface ImportResult {
  total: number;
  assigned: number;
  overwritten: number;
  unchanged: number;
  ottoUnknown: string[];
  varioNotFound: string[];
  priceSeeded?: number;
  priceFailures?: number;
  cancelled?: boolean;
  processed?: number;
}

/** App-Tab `/#/offers-import` (system.apps.{appId}.tabs). Paste/Upload von OTTOSKU;VARIOSKU-Paaren,
 *  Client-Vorschau, Enqueue via otto_enqueue_offers_import, Live-Fortschritt + End-Report. */
export function renderOffersImport(ctx: Ctx): void {
  ensureOttoStyles();
  ctx.contentEl.hidden = false;
  document.documentElement.classList.add('otto-autogrow');

  ctx.contentEl.innerHTML = `
    <div class="otto-install otto-set">
      <header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce"><img src="/logo-black.png" alt="go eCommerce"></a>
        <img class="otto-logo" src="/otto-market.png" alt="OTTO Market">
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">OTTO · Import</div><h1>Angebote-Import</h1></div>
        <div class="hactions"><span class="hstatus"><span class="d"></span>OTTO Market verbunden</span></div>
      </header>
      <div class="stage">
        <div class="card">
          <div class="card-body">
            <p class="intro">Ordne viele Otto-Angebote in einem Schritt Vario-Artikeln zu. Eine Zeile je Paar im Format <b>OTTOSKU;VARIOSKU</b> (VARIOSKU = Vario-Artikelnummer). Einfügen oder Datei (.csv/.txt) hochladen.</p>

            <div class="sec-lab">Eingabe</div>
            <textarea id="oi-input" class="inp" rows="10" style="font-family:var(--mono,monospace);resize:vertical;" placeholder="OTTOSKU;VARIOSKU&#10;A-1001;4711&#10;A-1002;4712"></textarea>
            <div class="oi-inputbar">
              <label class="btn btn-ghost" style="cursor:pointer;">${OTTO_ICON.info}Datei wählen…
                <input type="file" id="oi-file" accept=".csv,.txt,text/csv,text/plain" hidden>
              </label>
              <button class="btn btn-ghost" id="oi-clear" type="button">Leeren</button>
            </div>

            <div class="sec-lab" style="margin-top:24px;">Vorschau</div>
            <div id="oi-preview" class="foot-status">${OTTO_ICON.info}Noch keine Eingabe.</div>
            <div id="oi-preview-lists" style="margin-top:8px;"></div>

            <div id="oi-progress" hidden style="margin-top:16px;"></div>
            <div id="oi-report" hidden style="margin-top:16px;"></div>
          </div>
          <div class="card-foot">
            <span class="foot-status" id="oi-foot">${OTTO_ICON.info}Bereit</span>
            <div class="foot-actions">
              <button class="btn btn-primary" id="oi-start" disabled>${OTTO_ICON.info}Import starten</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <style>
      .otto-install [hidden]{display:none !important;}
      html.otto-autogrow, html.otto-autogrow body, html.otto-autogrow main#app{height:auto;overflow:visible;}
      html.otto-autogrow main#app > section#content{flex:0 0 auto;}
      .otto-install.otto-set{box-sizing:border-box;display:flex;flex-direction:column;height:auto;padding:16px 26px 20px;}
      .otto-install.otto-set .apphead{flex:none;margin-bottom:16px;}
      .otto-install.otto-set .otto-logo{height:24px;width:auto;flex:none;}
      .otto-install.otto-set .hactions{margin-left:auto;display:flex;gap:13px;align-items:center;}
      .otto-install.otto-set .hstatus{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;color:var(--muted);font-weight:500;}
      .otto-install.otto-set .hstatus .d{width:8px;height:8px;border-radius:50%;background:var(--success);box-shadow:0 0 0 3px rgba(34,197,94,.16);}
      .otto-install.otto-set .stage{display:flex;flex-direction:column;align-items:flex-start;padding:2px 4px;}
      .otto-install.otto-set .card{align-self:stretch;}
      .otto-install textarea.inp{width:100%;box-sizing:border-box;}
      /* Button-Zeile unter dem Textarea: „Datei wählen" + „Leeren" direkt nebeneinander.
         (Früher .lager-row — die Klasse ist nur im Settings-View definiert, hier ohne Wirkung.) */
      .oi-inputbar{display:flex;gap:8px;align-items:center;margin-top:10px;}
      .oi-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:10px;}
      .oi-tile{border:1px solid #E2E2E6;border-radius:10px;padding:12px 14px;background:#fff;}
      .oi-tile .n{font-size:20px;font-weight:700;color:var(--ink);}
      .oi-tile .l{font-size:11.5px;color:var(--muted);margin-top:2px;}
      .oi-list details{border:1px solid #E2E2E6;border-radius:9px;padding:8px 10px;margin-top:8px;background:#fff;}
      .oi-list summary{cursor:pointer;font-size:13px;color:var(--ink);}
      .oi-list pre{white-space:pre-wrap;word-break:break-all;font-size:12px;color:var(--ink-soft);margin:8px 0 0;max-height:200px;overflow:auto;}
    </style>
  `;

  const inputEl = ctx.contentEl.querySelector<HTMLTextAreaElement>('#oi-input')!;
  const fileEl = ctx.contentEl.querySelector<HTMLInputElement>('#oi-file')!;
  const clearBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#oi-clear')!;
  const previewEl = ctx.contentEl.querySelector<HTMLDivElement>('#oi-preview')!;
  const previewListsEl = ctx.contentEl.querySelector<HTMLDivElement>('#oi-preview-lists')!;
  const startBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#oi-start')!;
  const progressEl = ctx.contentEl.querySelector<HTMLDivElement>('#oi-progress')!;
  const reportEl = ctx.contentEl.querySelector<HTMLDivElement>('#oi-report')!;
  const footEl = ctx.contentEl.querySelector<HTMLSpanElement>('#oi-foot')!;

  let parsed: OfferImportParse = { pairs: [], invalidLines: [], duplicates: [] };

  const detailsBlock = (title: string, lines: string[]): string => {
    if (lines.length === 0) return '';
    const id = `oi-copy-${Math.random().toString(36).slice(2, 8)}`;
    return `<div class="oi-list"><details>
      <summary>${ottoEscapeHtml(title)} (${lines.length}) <button type="button" class="btn btn-ghost" data-copy="${id}" style="margin-left:8px;">Kopieren</button></summary>
      <pre id="${id}">${ottoEscapeHtml(lines.join('\n'))}</pre>
    </details></div>`;
  };
  const wireCopy = (root: HTMLElement) => {
    root.querySelectorAll<HTMLButtonElement>('button[data-copy]').forEach((b) => {
      b.addEventListener('click', () => {
        const pre = root.querySelector<HTMLPreElement>(`#${b.dataset['copy']}`);
        if (pre) { void navigator.clipboard?.writeText(pre.textContent ?? ''); showToast('In Zwischenablage kopiert', 'success'); }
      });
    });
  };

  const refreshPreview = () => {
    parsed = parseOfferImport(inputEl.value);
    const n = parsed.pairs.length;
    previewEl.className = n > 0 ? 'foot-status ready' : 'foot-status';
    previewEl.innerHTML = n > 0
      ? `${OTTO_ICON.check}${n} gültige Paare · ${parsed.invalidLines.length} ungültige Zeilen · ${parsed.duplicates.length} Duplikate`
      : `${OTTO_ICON.info}Keine gültigen Paare erkannt.`;
    previewListsEl.innerHTML =
      detailsBlock('Ungültige Zeilen', parsed.invalidLines.map((l) => `Zeile ${l.line}: ${l.raw}`)) +
      detailsBlock('Duplikate (letzte Zeile gewinnt)', parsed.duplicates);
    wireCopy(previewListsEl);
    startBtn.disabled = n === 0;
    startBtn.innerHTML = `${OTTO_ICON.info}Import starten${n > 0 ? ` (${n})` : ''}`;
  };

  inputEl.addEventListener('input', refreshPreview);
  clearBtn.addEventListener('click', () => { inputEl.value = ''; refreshPreview(); reportEl.hidden = true; progressEl.hidden = true; });
  fileEl.addEventListener('change', async () => {
    const f = fileEl.files?.[0];
    if (!f) return;
    inputEl.value = await f.text();
    fileEl.value = '';
    refreshPreview();
  });

  startBtn.addEventListener('click', async () => {
    if (parsed.pairs.length === 0) return;
    startBtn.disabled = true;
    inputEl.disabled = true;
    reportEl.hidden = true;
    progressEl.hidden = false;
    progressEl.innerHTML = `<span class="foot-status"><span class="spin"></span>Job wird gestartet …</span>`;
    footEl.className = 'foot-status';
    footEl.innerHTML = `${OTTO_ICON.info}Import läuft`;
    try {
      const { data, error } = await ctx.supabase.rpc('otto_enqueue_offers_import', { _pairs: parsed.pairs });
      if (error) throw new Error(error.message);
      const jobId = typeof data === 'string' ? data : Array.isArray(data) ? (data[0] as string) : null;
      if (!jobId) throw new Error('Keine Job-ID erhalten');

      const job: JobRow = await awaitJob(ctx.supabase, jobId, {
        timeoutMs: 30 * 60_000,
        pollIntervalMs: 1500,
        onProgress: (row) => {
          const p = (row.progress ?? {}) as Record<string, number>;
          if (p.total) {
            progressEl.innerHTML = `<span class="foot-status"><span class="spin"></span>Verarbeite ${p.processed ?? 0} / ${p.total} …</span>`;
          }
        },
      });
      progressEl.hidden = true;
      if (job.status !== 'completed') {
        footEl.className = 'foot-status error';
        footEl.innerHTML = `${OTTO_ICON.info}Import ${job.status === 'cancelled' ? 'abgebrochen' : 'fehlgeschlagen'}${job.error ? `: ${ottoEscapeHtml(job.error)}` : ''}`;
        return;
      }
      const result = job.result as ImportResult;
      renderReport(result);
      if (result.cancelled === true) {
        footEl.className = 'foot-status error';
        footEl.innerHTML = `${OTTO_ICON.info}Import abgebrochen — Teil-Ergebnis (${result.processed ?? 0} / ${result.total ?? 0})`;
      } else {
        footEl.className = 'foot-status ready';
        footEl.innerHTML = `${OTTO_ICON.check}Import abgeschlossen`;
        showToast('Angebots-Import abgeschlossen.', 'success');
      }
    } catch (e) {
      progressEl.hidden = true;
      footEl.className = 'foot-status error';
      footEl.innerHTML = `${OTTO_ICON.info}Fehler: ${ottoEscapeHtml((e as Error).message)}`;
    } finally {
      inputEl.disabled = false;
      startBtn.disabled = parsed.pairs.length === 0;
    }
  });

  function renderReport(r: ImportResult): void {
    const tile = (n: number, l: string) => `<div class="oi-tile"><div class="n">${n}</div><div class="l">${l}</div></div>`;
    const cancelledBanner = r.cancelled === true
      ? `<div class="foot-status error" style="margin-bottom:10px;">${OTTO_ICON.info}Import abgebrochen — nur ${ottoEscapeHtml(String(r.processed ?? 0))} von ${ottoEscapeHtml(String(r.total ?? 0))} Zeilen verarbeitet.</div>`
      : '';
    reportEl.hidden = false;
    reportEl.innerHTML = `
      <div class="sec-lab">Ergebnis</div>
      ${cancelledBanner}
      <div class="oi-tiles">
        ${tile(r.assigned ?? 0, 'zugeordnet')}
        ${tile(r.overwritten ?? 0, 'überschrieben')}
        ${tile(r.unchanged ?? 0, 'unverändert')}
        ${tile((r.ottoUnknown ?? []).length, 'OTTO-SKU unbekannt')}
        ${tile((r.varioNotFound ?? []).length, 'VARIO-SKU nicht gefunden')}
      </div>
      <div id="oi-report-lists" style="margin-top:10px;">
        ${detailsBlock('OTTO-SKU unbekannt', r.ottoUnknown ?? [])}
        ${detailsBlock('VARIO-SKU nicht gefunden', r.varioNotFound ?? [])}
        ${detailsBlock('Ungültige Zeilen (aus Datei)', parsed.invalidLines.map((l) => `Zeile ${l.line}: ${l.raw}`))}
        ${detailsBlock('Duplikate (aus Datei)', parsed.duplicates)}
      </div>`;
    wireCopy(reportEl);
  }

  refreshPreview();
}
